import { type RefObject, useEffect, useState } from 'react';

/** Where a pinned bar sits: the card's left edge and width, in viewport pixels. */
export interface PinnedBox {
    left: number;
    width: number;
}

/**
 * Whether the card's bar pins now: its slot, at the card's end, is cut off by the viewport's
 * bottom edge, and the card's body reaches the strip the pinned bar takes there. Only then is
 * the bar over its own card; a card that only peeks into that strip leaves it to the card
 * above, whose bar sits there in flow, so one bar pins at a time.
 */
function pinnedBox(card: HTMLElement, slot: HTMLElement, barHeight: number): PinnedBox | null {
    const viewport = window.innerHeight;
    const cardRect = card.getBoundingClientRect();
    const slotBelow = slot.getBoundingClientRect().bottom > viewport;
    const reachesBar = cardRect.top < viewport - barHeight && cardRect.bottom > 0;
    return slotBelow && reachesBar ? { left: cardRect.left, width: cardRect.width } : null;
}

function sameBox(a: PinnedBox | null, b: PinnedBox | null): boolean {
    return a === b || (a !== null && b !== null && a.left === b.left && a.width === b.width);
}

/**
 * Where the card's save bar pins to the viewport bottom, or null to leave it in flow.
 * AppLayout's content column scrolls with the window rather than inside itself, so
 * `position: sticky` never pins there. The bar is placed again on every scroll and resize
 * (once per frame), when the card or the content column changes size (the sidebar opening or
 * collapsing moves a centred card), and when IntersectionObserver sees the card or its slot
 * cross the viewport. Without IntersectionObserver the bar stays in flow.
 */
export function usePinnedFooter(
    cardRef: RefObject<HTMLElement | null>,
    slotRef: RefObject<HTMLElement | null>,
    barHeight: number
): PinnedBox | null {
    const [box, setBox] = useState<PinnedBox | null>(null);

    useEffect(() => {
        const card = cardRef.current;
        const slot = slotRef.current;
        if (card === null || slot === null || typeof IntersectionObserver === 'undefined') return;
        let frame: number | null = null;
        const measure = (): void => {
            frame = null;
            const next = pinnedBox(card, slot, barHeight);
            setBox((previous) => (sameBox(previous, next) ? previous : next));
        };
        const schedule = (): void => {
            if (frame === null) frame = window.requestAnimationFrame(measure);
        };
        const crossing = new IntersectionObserver(measure, { threshold: [0, 1] });
        crossing.observe(card);
        crossing.observe(slot);
        const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        resize?.observe(card);
        const column = card.closest('main');
        if (column !== null) resize?.observe(column);
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        measure();
        return () => {
            if (frame !== null) window.cancelAnimationFrame(frame);
            crossing.disconnect();
            resize?.disconnect();
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, [cardRef, slotRef, barHeight]);

    return box;
}
